import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";

import PublicShell from "./PublicShell";
import { SITE } from "../../config/site";

/* -------------------------------------------------------------------------- */
/* Formatting                                                                 */
/* -------------------------------------------------------------------------- */

function validDate(value) {
  if (!value) return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getStartValue(event) {
  return event?.timing?.startTime || event?.startTime || null;
}

function getEndValue(event) {
  return event?.timing?.endTime || event?.endTime || null;
}

function formatDate(value) {
  const date = validDate(value);

  if (!date) {
    return "Date to be announced";
  }

  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year:
      date.getFullYear() !== new Date().getFullYear()
        ? "numeric"
        : undefined,
  });
}

function formatTime(value) {
  const date = validDate(value);

  if (!date) {
    return "";
  }

  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatTimeRange(event) {
  const start = formatTime(getStartValue(event));
  const end = formatTime(getEndValue(event));

  if (!start) {
    return "Time to be announced";
  }

  if (!end) {
    return start;
  }

  return `${start} – ${end}`;
}

function getImageUrl(event) {
  return (
    event?.media?.coverImageUrl ||
    event?.coverImageUrl ||
    event?.imageUrl ||
    ""
  );
}

function getRegistrationUrl(event) {
  return (
    event?.registration?.primaryUrl ||
    event?.registration?.ticketUrl ||
    event?.registration?.externalUrl ||
    event?.ticketUrl ||
    event?.externalUrl ||
    event?.sourceMeta?.ticketUrl ||
    event?.sourceMeta?.externalUrl ||
    event?.sourceMeta?.websiteUrl ||
    ""
  );
}

function getPriceLabel(event) {
  if (event?.pricing?.label) {
    return event.pricing.label;
  }

  if (event?.pricing?.displayText) {
    return event.pricing.displayText;
  }

  if (event?.pricing?.isFree) {
    return "Free";
  }

  const min = Number(event?.pricing?.minAmount);
  const max = Number(event?.pricing?.maxAmount);

  const hasMin = Number.isFinite(min);
  const hasMax = Number.isFinite(max);

  if (!hasMin && !hasMax) {
    return "";
  }

  const currency =
    event?.pricing?.currency === "USD" ||
    !event?.pricing?.currency
      ? "$"
      : `${event.pricing.currency} `;

  if (hasMin && hasMax && min !== max) {
    return `${currency}${min} – ${currency}${max}`;
  }

  const amount = hasMin ? min : max;

  return `${currency}${amount}`;
}

function getCapacityLabel(event) {
  const capacity = Number(event?.capacity);

  if (!Number.isFinite(capacity) || capacity < 1) {
    return "";
  }

  return `${Math.floor(capacity)} spots`;
}

function getRegistrationLabel(event) {
  if (event?.registration?.required) {
    return "Required";
  }

  if (getRegistrationUrl(event)) {
    return "Available";
  }

  return "";
}

function getLocationName(event) {
  return String(
    event?.location?.displayName ||
      event?.location?.name ||
      ""
  ).trim();
}

function getLocationAddress(event) {
  return String(event?.location?.address || "").trim();
}

function getLocationLabel(event) {
  return (
    getLocationName(event) ||
    getLocationAddress(event) ||
    ""
  );
}

function getMapsUrl(event) {
  const address = getLocationAddress(event);

  if (address) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      address
    )}`;
  }

  const latitude = Number(event?.location?.latitude);
  const longitude = Number(event?.location?.longitude);

  if (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude)
  ) {
    return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
  }

  return "";
}

function isImportedEvent(event) {
  return Boolean(
    event?.source?.imported ||
      event?.source?.provider ||
      event?.sourceProvider ||
      event?.sourceMeta?.provider ||
      event?.origin?.provider
  );
}

function getAttribution(event) {
  const imported = isImportedEvent(event);

  const organizer = String(
    event?.organizer?.name ||
      event?.organizerName ||
      ""
  ).trim();

  const posterName = String(
    event?.poster?.displayName ||
      event?.displayPosterName ||
      event?.poster?.username ||
      event?.createdBy?.displayName ||
      event?.createdBy?.username ||
      ""
  ).trim();

  if (!imported) {
    return {
      label: "Hosted by",
      name: posterName || organizer || "Event host",
    };
  }

  if (organizer) {
    return {
      label: "Organized by",
      name: organizer,
    };
  }

  return {
    label: "Presented by",
    name: posterName || "Veacon",
  };
}

function getSourceLabel(event) {
  return (
    event?.origin?.displayName ||
    event?.sourceMeta?.sourceDisplayName ||
    event?.sourceMeta?.platformName ||
    ""
  );
}

/* -------------------------------------------------------------------------- */
/* Components                                                                 */
/* -------------------------------------------------------------------------- */

function Pill({
  children,
  tone = "default",
}) {
  const toneClass =
    tone === "event"
      ? "border-[rgb(var(--event)/0.35)] bg-[rgb(var(--event)/0.10)] text-[rgb(var(--event))]"
      : "border-[rgb(var(--border))] bg-[rgb(var(--elevated))] text-[rgb(var(--text-muted))]";

  return (
    <span
      className={`
        inline-flex items-center
        rounded-full border
        px-3 py-1.5
        text-xs font-black
        ${toneClass}
      `}
    >
      {children}
    </span>
  );
}

function DetailRow({
  label,
  value,
  href,
}) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  const valueContent = href ? (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="
        text-right font-bold
        text-[rgb(var(--event))]
        underline
        decoration-[rgb(var(--event)/0.3)]
        underline-offset-4
        transition-opacity
        hover:opacity-70
      "
    >
      {value} ↗
    </a>
  ) : (
    <div className="text-right font-bold text-[rgb(var(--text-main))]">
      {value}
    </div>
  );

  return (
    <div
      className="
        grid grid-cols-[minmax(0,0.42fr)_minmax(0,0.58fr)]
        gap-4
        border-b border-[rgb(var(--border))]
        py-3
        text-sm
        last:border-b-0
      "
    >
      <div className="font-bold text-[rgb(var(--text-muted))]">
        {label}
      </div>

      {valueContent}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="text-xl font-black tracking-tight sm:text-2xl">
      {children}
    </h2>
  );
}

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

export default function PublicEventPage() {
  const { eventId } = useParams();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [descriptionOpen, setDescriptionOpen] =
    useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadEvent() {
      try {
        setLoading(true);
        setError("");

        const response = await axios.get(
          `/api/public/events/${encodeURIComponent(
            eventId || ""
          )}`
        );

        if (cancelled) {
          return;
        }

        setEvent(response?.data?.event || null);
      } catch (requestError) {
        if (cancelled) {
          return;
        }

        if (requestError?.response?.status === 404) {
          setError("This event is unavailable.");
        } else {
          setError(
            "Couldn’t load this event right now."
          );
        }

        setEvent(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadEvent();

    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const dateLabel = useMemo(
    () => formatDate(getStartValue(event)),
    [event]
  );

  const timeLabel = useMemo(
    () => formatTimeRange(event),
    [event]
  );

  if (loading) {
    return (
      <PublicShell>
        <section
          className="
            rounded-[2rem]
            border border-[rgb(var(--border))]
            bg-[rgb(var(--surface))]
            p-7
            shadow-sm
            sm:p-9
          "
        >
          <div className="text-lg font-black">
            Loading event…
          </div>

          <p className="mt-2 text-sm text-[rgb(var(--text-muted))]">
            Getting the latest event details.
          </p>
        </section>
      </PublicShell>
    );
  }

  if (error || !event) {
    return (
      <PublicShell>
        <section
          className="
            rounded-[2rem]
            border border-[rgb(var(--border))]
            bg-[rgb(var(--surface))]
            p-7
            text-center
            shadow-sm
            sm:p-10
          "
        >
          <div className="text-2xl font-black tracking-tight">
            Event unavailable
          </div>

          <p className="mt-3 text-sm leading-6 text-[rgb(var(--text-muted))] sm:text-base">
            {error ||
              "This event could not be found."}
          </p>

          <p className="mx-auto mt-2 max-w-md text-xs leading-5 text-[rgb(var(--text-muted))]">
            Private, cancelled, and unavailable
            events aren't shown on public Veacon
            pages.
          </p>

          <Link
            to="/"
            className="
              mt-6 inline-flex
              items-center justify-center
              rounded-2xl
              border border-[rgb(var(--border))]
              bg-[rgb(var(--surface))]
              px-5 py-3
              font-black
              transition-colors
              hover:bg-[rgb(var(--elevated))]
            "
          >
            Explore Veacon
          </Link>
        </section>
      </PublicShell>
    );
  }

  const safeEventId =
    event?.id || event?._id || eventId;

  const appUrl =
    `veacon://events/${encodeURIComponent(
      String(safeEventId)
    )}`;

  const imageUrl = getImageUrl(event);
  const registrationUrl =
    getRegistrationUrl(event);

  const price = getPriceLabel(event);
  const registration =
    getRegistrationLabel(event);
  const capacity = getCapacityLabel(event);

  const locationName = getLocationName(event);
  const locationAddress =
    getLocationAddress(event);
  const locationLabel = getLocationLabel(event);
  const mapsUrl = getMapsUrl(event);

  const attribution = getAttribution(event);
  const sourceLabel = getSourceLabel(event);

  const interestedCount =
    Number(
      event?.socialSignals?.interestedCount ??
        event?.interestedCount ??
        0
    ) || 0;

  const goingCount =
    Number(
      event?.socialSignals?.goingCount ??
        event?.goingCount ??
        0
    ) || 0;

  const description = String(
    event?.description || ""
  ).trim();

  const displayedDescription =
    !descriptionOpen && description.length > 700
      ? `${description.slice(0, 700).trim()}…`
      : description;

  const organizerContact = String(
    event?.organizer?.contact ||
      event?.organizerContact ||
      ""
  ).trim();

  const faq = Array.isArray(event?.faq)
    ? event.faq.filter(
        (item) =>
          item?.question || item?.answer
      )
    : [];

  const tags = Array.isArray(event?.tags)
    ? event.tags.filter(Boolean).slice(0, 12)
    : [];

  const hasEventDetails = Boolean(
    capacity ||
      organizerContact ||
      event?.eventInfo?.parkingInfo ||
      event?.eventInfo?.ageRestrictions ||
      event?.eventInfo?.entryRequirements ||
      event?.eventInfo?.dressCode ||
      event?.eventInfo?.refundPolicy
  );

  return (
    <PublicShell>
      <article
        className="
          overflow-hidden
          rounded-[2.25rem]
          border border-[rgb(var(--border))]
          bg-[rgb(var(--surface))]
          shadow-[var(--shadow-soft)]
        "
      >
        {/* Cover ----------------------------------------------------------- */}

        {imageUrl ? (
          <div
            className="
              relative
              max-h-[560px]
              w-full
              overflow-hidden
              bg-[rgb(var(--elevated))]
            "
          >
            <img
              src={imageUrl}
              alt=""
              className="
                max-h-[560px]
                w-full
                object-cover
              "
            />

            <div
              className="
                pointer-events-none
                absolute inset-x-0 bottom-0
                h-32
                bg-gradient-to-t
                from-black/35
                to-transparent
              "
            />
          </div>
        ) : (
          <div
            className="
              flex h-44
              items-center justify-center
              bg-[rgb(var(--event)/0.10)]
              sm:h-56
            "
          >
            <span
              className="
                text-xs font-black
                uppercase tracking-[0.18em]
                text-[rgb(var(--event))]
              "
            >
              Veacon Event
            </span>
          </div>
        )}

        {/* Hero information ------------------------------------------------ */}

        <div className="px-5 py-7 sm:px-8 sm:py-9 lg:px-10 lg:py-10">
          <div className="flex flex-wrap gap-2">
            <Pill tone="event">
              {event?.category || "Event"}
            </Pill>

            {sourceLabel ? (
              <Pill>{sourceLabel}</Pill>
            ) : null}
          </div>

          <div
            className="
              mt-5
              text-xs font-black
              uppercase tracking-[0.12em]
              text-[rgb(var(--event))]
              sm:text-sm
            "
          >
            {dateLabel}
          </div>

          <h1
            className="
              mt-2
              max-w-4xl
              text-3xl font-black
              leading-[1.05]
              tracking-[-0.04em]
              sm:text-4xl
              lg:text-5xl
            "
          >
            {event?.title || "Veacon event"}
          </h1>

          <div className="mt-5 space-y-1">
            {timeLabel ? (
              <div className="text-base font-black sm:text-lg">
                {timeLabel}
              </div>
            ) : null}

            {locationName ? (
              <div className="text-sm font-bold text-[rgb(var(--text-muted))] sm:text-base">
                {locationName}
              </div>
            ) : null}

            {!locationName && locationLabel ? (
              <div className="text-sm font-bold text-[rgb(var(--text-muted))] sm:text-base">
                {locationLabel}
              </div>
            ) : null}

            {locationAddress ? (
              mapsUrl ? (
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="
                    inline-block
                    text-sm font-bold
                    text-[rgb(var(--event))]
                    transition-opacity
                    hover:opacity-70
                  "
                >
                  {locationAddress} ↗
                </a>
              ) : (
                <div className="text-sm text-[rgb(var(--text-muted))]">
                  {locationAddress}
                </div>
              )
            ) : null}
          </div>

          {attribution?.name ? (
            <div className="mt-4 text-sm text-[rgb(var(--text-muted))]">
              {attribution.label}{" "}
              <span className="font-black text-[rgb(var(--text-main))]">
                {attribution.name}
              </span>
            </div>
          ) : null}

          {/* Primary actions ------------------------------------------------ */}

          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <a
              href={appUrl}
              className="
                inline-flex min-h-12
                items-center justify-center
                rounded-2xl
                bg-[rgb(var(--event))]
                px-6 py-3
                font-black text-white
                transition
                hover:opacity-90
              "
            >
              Open in Veacon
            </a>

            {registrationUrl ? (
              <a
                href={registrationUrl}
                target="_blank"
                rel="noreferrer"
                className="
                  inline-flex min-h-12
                  items-center justify-center
                  rounded-2xl
                  border border-[rgb(var(--border))]
                  bg-[rgb(var(--surface))]
                  px-6 py-3
                  font-black
                  transition-colors
                  hover:bg-[rgb(var(--elevated))]
                "
              >
                Tickets / Registration ↗
              </a>
            ) : null}
          </div>

          {/* Social counts -------------------------------------------------- */}

          {goingCount > 0 ||
          interestedCount > 0 ? (
            <div className="mt-6 flex flex-wrap gap-2">
              {goingCount > 0 ? (
                <Pill>
                  {goingCount}{" "}
                  {goingCount === 1
                    ? "person going"
                    : "going"}
                </Pill>
              ) : null}

              {interestedCount > 0 ? (
                <Pill>
                  {interestedCount} interested
                </Pill>
              ) : null}
            </div>
          ) : null}

          {/* Veacon bridge -------------------------------------------------- */}

          <section
            className="
              relative mt-8
              overflow-hidden
              rounded-[1.75rem]
              border border-[rgb(var(--plan)/0.25)]
              bg-[rgb(var(--plan)/0.08)]
              p-5
              sm:p-6
            "
          >
            <div
              className="
                pointer-events-none
                absolute -right-20 -top-20
                h-52 w-52
                rounded-full blur-3xl
              "
              style={{
                background:
                  "radial-gradient(circle, rgb(var(--plan) / 0.16), transparent 70%)",
              }}
            />

            <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="max-w-xl">
                <div className="text-lg font-black tracking-tight">
                  Make plans around this event
                </div>

                <p className="mt-2 text-sm leading-6 text-[rgb(var(--text-muted))]">
                  See who's interested, mark yourself
                  going, and turn this event into a plan
                  with friends in Veacon.
                </p>
              </div>

              <a
                href={appUrl}
                className="
                  inline-flex shrink-0
                  items-center justify-center
                  rounded-2xl
                  bg-[rgb(var(--plan))]
                  px-5 py-3
                  text-sm font-black
                  text-white
                  transition
                  hover:opacity-90
                "
              >
                Open Veacon →
              </a>
            </div>
          </section>

          {/* Main content --------------------------------------------------- */}

          <div
            className="
              mt-10 grid
              gap-10
              lg:grid-cols-[minmax(0,1fr)_300px]
              lg:items-start
            "
          >
            <div className="min-w-0">
              {description ? (
                <section>
                  <SectionTitle>About</SectionTitle>

                  <p
                    className="
                      mt-4
                      whitespace-pre-line
                      text-sm leading-7
                      text-[rgb(var(--text-muted))]
                      sm:text-base
                    "
                  >
                    {displayedDescription}
                  </p>

                  {description.length > 700 ? (
                    <button
                      type="button"
                      onClick={() =>
                        setDescriptionOpen(
                          (value) => !value
                        )
                      }
                      className="
                        mt-3
                        text-sm font-black
                        text-[rgb(var(--event))]
                        transition-opacity
                        hover:opacity-70
                      "
                    >
                      {descriptionOpen
                        ? "Show less"
                        : "Read more"}
                    </button>
                  ) : null}
                </section>
              ) : null}

              {faq.length > 0 ? (
                <section className={description ? "mt-10" : ""}>
                  <SectionTitle>FAQ</SectionTitle>

                  <div className="mt-3">
                    {faq.map((item, index) => (
                      <div
                        key={`${
                          item?.question || "faq"
                        }-${index}`}
                        className="
                          border-b
                          border-[rgb(var(--border))]
                          py-4
                          last:border-b-0
                        "
                      >
                        {item?.question ? (
                          <h3 className="text-sm font-black leading-6 sm:text-base">
                            {item.question}
                          </h3>
                        ) : null}

                        {item?.answer ? (
                          <p
                            className={`
                              text-sm leading-6
                              text-[rgb(var(--text-muted))]
                              ${
                                item?.question
                                  ? "mt-1.5"
                                  : ""
                              }
                            `}
                          >
                            {item.answer}
                          </p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}

              {tags.length > 0 ? (
                <section className="mt-9">
                  <h2 className="text-sm font-black">
                    Tags
                  </h2>

                  <div className="mt-3 flex flex-wrap gap-x-3 gap-y-2">
                    {tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs font-bold text-[rgb(var(--text-muted))]"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </section>
              ) : null}
            </div>

            {/* Sidebar ------------------------------------------------------ */}

            <aside className="space-y-5">
              {(registrationUrl ||
                price ||
                registration) ? (
                <section
                  className="
                    rounded-[1.5rem]
                    border border-[rgb(var(--border))]
                    bg-[rgb(var(--elevated))]
                    p-5
                  "
                >
                  <h2 className="text-lg font-black">
                    Tickets
                  </h2>

                  {price || registration ? (
                    <p className="mt-2 text-sm leading-6 text-[rgb(var(--text-muted))]">
                      {[
                        price,
                        registration
                          ? `Registration ${registration.toLowerCase()}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  ) : null}

                  {registrationUrl ? (
                    <a
                      href={registrationUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="
                        mt-4 inline-flex
                        items-center
                        text-sm font-black
                        text-[rgb(var(--event))]
                        transition-opacity
                        hover:opacity-70
                      "
                    >
                      View tickets →
                    </a>
                  ) : null}
                </section>
              ) : null}

              {hasEventDetails ? (
                <section
                  className="
                    rounded-[1.5rem]
                    border border-[rgb(var(--border))]
                    bg-[rgb(var(--surface))]
                    p-5
                  "
                >
                  <h2 className="text-lg font-black">
                    Event details
                  </h2>

                  <div className="mt-3 border-t border-[rgb(var(--border))]">
                    <DetailRow
                      label="Capacity"
                      value={capacity}
                    />

                    <DetailRow
                      label="Organizer contact"
                      value={organizerContact}
                    />

                    <DetailRow
                      label="Parking"
                      value={
                        event?.eventInfo
                          ?.parkingInfo
                      }
                    />

                    <DetailRow
                      label="Age restrictions"
                      value={
                        event?.eventInfo
                          ?.ageRestrictions
                      }
                    />

                    <DetailRow
                      label="Entry requirements"
                      value={
                        event?.eventInfo
                          ?.entryRequirements
                      }
                    />

                    <DetailRow
                      label="Dress code"
                      value={
                        event?.eventInfo
                          ?.dressCode
                      }
                    />

                    <DetailRow
                      label="Refund policy"
                      value={
                        event?.eventInfo
                          ?.refundPolicy
                      }
                    />
                  </div>
                </section>
              ) : null}

              <section
                className="
                  rounded-[1.5rem]
                  border border-[rgb(var(--border))]
                  bg-[rgb(var(--surface))]
                  p-5
                "
              >
                <div className="text-sm font-black">
                  Discover more with Veacon
                </div>

                <p className="mt-2 text-xs leading-5 text-[rgb(var(--text-muted))]">
                  Find nearby events, see what fits,
                  and make plans with friends.
                </p>

                <a
                  href={SITE.appStoreUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="
                    mt-4 inline-flex
                    text-sm font-black
                    text-[rgb(var(--brand))]
                    transition-opacity
                    hover:opacity-70
                  "
                >
                  Download Veacon →
                </a>
              </section>
            </aside>
          </div>
        </div>
      </article>
    </PublicShell>
  );
}